import { Link, useLocation } from 'react-router-dom';

import { OptionsMenu } from './components/OptionsMenu';
import type { HeaderOptionItem } from './desktopContext';
import { HEADER_HEIGHT_PX } from './layout-tokens';
import {
  ACCOUNT_PASSWORD_LABEL,
  ACCOUNT_PASSWORD_PATH,
  type AccountPasswordLocationState,
} from './routes';
import { StatusBadge } from './StatusBadge';
import { SessionPersistenceNotice, SignOutButton } from '../lib/auth/SessionControls';
import { describePermissionsState, describePlantScope } from '../lib/permissions/client';
import type { PermissionsState } from '../lib/permissions/types';

export interface Breadcrumb {
  readonly label: string;
  readonly to?: string;
}

export interface HeaderBarProps {
  readonly breadcrumbs: readonly Breadcrumb[];
  readonly sidebarExpanded: boolean;
  readonly onToggleSidebar: () => void;
  readonly permissions: PermissionsState;
  /** รายการที่หน้าจอปัจจุบันลงทะเบียนผ่าน `useHeaderOptionItems()` (`WO-P2-M11-023`, D-142) */
  readonly pageOptionItems?: readonly HeaderOptionItem[];
}

interface Identity {
  readonly name: string;
  readonly department: string;
  readonly plantScope: string;
}

/**
 * "คุณคือใคร และเห็นอะไรได้บ้าง" จาก permission set ที่เซิร์ฟเวอร์ยืนยันแล้ว
 *
 * deny-safe: ทุกสถานะที่ไม่ใช่ `ready` แสดงว่ายังไม่ระบุตัวตน ห้ามเดาชื่อ แผนก
 * หรือขอบเขตโรงงานจากอะไรก็ตามที่ค้างอยู่ในเบราว์เซอร์
 */
function identityOf(permissions: PermissionsState): Identity {
  if (permissions.status !== 'ready') {
    return {
      name: 'ยังไม่ระบุตัวตน',
      department: 'ยังไม่ยืนยันสิทธิ์',
      plantScope: 'ยังไม่ทราบขอบเขตโรงงาน',
    };
  }
  const { display_name, employee_id, department_name, plant_scope } = permissions.permissions;
  return {
    name: display_name ?? employee_id,
    department: department_name ?? 'ไม่สังกัดแผนก',
    plantScope: describePlantScope(plant_scope),
  };
}

export function HeaderBar({
  breadcrumbs,
  sidebarExpanded,
  onToggleSidebar,
  permissions,
  pageOptionItems = [],
}: HeaderBarProps) {
  const verified = permissions.status === 'ready';
  const identity = identityOf(permissions);
  const { pathname } = useLocation();
  const onPasswordScreen = pathname === ACCOUNT_PASSWORD_PATH;

  return (
    <header
      className="cap-header"
      data-testid="desktop-header"
      style={{ height: `${HEADER_HEIGHT_PX}px` }}
    >
      <button
        type="button"
        className="cap-header__menu"
        onClick={onToggleSidebar}
        aria-expanded={sidebarExpanded}
        aria-controls="cap-desktop-sidebar"
        aria-label="สลับการแสดงเมนูหลัก"
      >
        <span aria-hidden="true">☰</span>
      </button>

      <nav className="cap-header__crumbs" aria-label="เส้นทางหน้าจอ">
        <ol>
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            return (
              <li key={crumb.label} className={isLast ? 'cap-crumb cap-crumb--leaf' : 'cap-crumb'}>
                {/*
                  crumb ตัวสุดท้ายเป็นข้อความล้วนเสมอ ไม่ว่าจะมี `to` มาด้วยหรือไม่
                  (มันคือหน้าที่กำลังยืนอยู่ — ลิงก์กลับไปหาตัวเองไม่มีความหมาย)
                  ส่วน crumb ที่เหลือ ถ้ามี `to` จะเป็นลิงก์จริง — `Breadcrumb.to`
                  มีมาตั้งแต่ต้นแต่ไม่เคยถูกใช้จนถึง Work Order นี้ (D-107 ข้อ 6)
                */}
                {isLast ? (
                  <span aria-current="page">{crumb.label}</span>
                ) : crumb.to !== undefined ? (
                  <Link to={crumb.to}>{crumb.label}</Link>
                ) : (
                  <span>{crumb.label}</span>
                )}
                {isLast ? null : (
                  <span className="cap-crumb__sep" aria-hidden="true">
                    /
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>

      <div className="cap-header__meta">
        <StatusBadge tone="info" title="Phase 1 ทำงานแบบ Push เท่านั้น (D-034)">
          โหมดเติมเต็ม: PUSH
        </StatusBadge>
        <StatusBadge
          tone={verified ? 'success' : 'warning'}
          title={describePermissionsState(permissions)}
        >
          {verified ? 'ยืนยันสิทธิ์แล้ว' : 'ยังไม่ยืนยันสิทธิ์'}
        </StatusBadge>
        <div className="cap-header__divider" aria-hidden="true" />
        <div className="cap-header__user" data-testid="header-identity">
          <div className="cap-header__user-name" data-testid="header-employee">
            {identity.name}
          </div>
          <div className="cap-header__user-sub">
            <span data-testid="header-department">{identity.department}</span>
            <span aria-hidden="true"> · </span>
            {/* marker `ALL` ของ AUDITOR ต้องไม่หลุดออกมาดิบ ๆ ให้อ่านเหมือนรหัสโรงงาน */}
            <span data-testid="header-plant-scope">{identity.plantScope}</span>
          </div>
          <SessionPersistenceNotice className="cap-header__session-note" />
        </div>

        {/*
          `WO-P2-M11-004` (D-107 ข้อ 7) — เปลี่ยนรหัสผ่านและออกจากระบบย้ายจากปุ่ม
          แยกบน header เข้ามาอยู่ในเมนู Options เดียวกัน ทับการตัดสินเดิมของไฟล์นี้
          ที่เคยให้ทั้งสองปุ่มเห็นตลอดเวลาโดยไม่ซ่อนในเมนูย่อย — **เหตุผลเดิมยังจริง
          อยู่** เพียงแต่ถูกย้ายไปคุ้มครองที่ตัวปุ่ม Options แทนตัวลิงก์แต่ละอัน:

          - **ไม่มีการตรวจสิทธิ์โมดูลใด ๆ กับปุ่ม Options เอง** ทุกคนที่เข้าสู่ระบบ
            ได้ย่อมมีรหัสผ่านของตัวเองให้เปลี่ยนและต้องออกจากระบบได้เสมอ ถ้าวันหนึ่ง
            มีคนคิดจะเติม `can(...)` ให้ปุ่มนี้หรือเนื้อหาข้างในมัน โปรดอ่าน
            `desktop/routes.ts` ก่อน — RBAC matrix ไม่มีโมดูลสำหรับสองคำสั่งนี้ และ
            การซ่อนปุ่มจะทำให้ผู้ใช้ที่ยังไม่ได้รับสิทธิ์ใด ๆ เปลี่ยนรหัสผ่านหรือ
            ออกจากระบบไม่ได้เลย รวมถึงตอนที่ยืนอยู่บนหน้าเปลี่ยนรหัสผ่านเองด้วย
          - D-046 ให้ session ยาวเพื่อไม่ต้องพิมพ์รหัสซ้ำระหว่างกะ ราคาที่จ่ายคือ
            เครื่องที่ถูกวางทิ้งยังใช้งานได้ — ปุ่ม Options (ไม่ใช่แค่สิ่งที่อยู่
            ข้างใน) จึงต้อง render และเปิดได้เสมอโดยไม่มีเงื่อนไข

          `state.from` คือหน้าจอที่ผู้ใช้กำลังอยู่ เพื่อให้กดปิดแล้วกลับมาที่เดิม
        */}
        <OptionsMenu triggerLabel="ตัวเลือก">
          {pageOptionItems.length > 0 ? (
            <>
              {pageOptionItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="cap-button cap-button--ghost cap-options__item"
                  disabled={item.disabled}
                  onClick={item.onSelect}
                  {...(item.testId !== undefined ? { 'data-testid': item.testId } : {})}
                >
                  {item.label}
                </button>
              ))}
              <div className="cap-options__separator" role="separator" />
            </>
          ) : null}
          <Link
            className="cap-button cap-button--ghost cap-options__item"
            data-testid="header-change-password"
            to={ACCOUNT_PASSWORD_PATH}
            {...(onPasswordScreen
              ? { 'aria-current': 'page' as const }
              : { state: { from: pathname } satisfies AccountPasswordLocationState })}
          >
            {ACCOUNT_PASSWORD_LABEL}
          </Link>
          <SignOutButton className="cap-button cap-button--ghost cap-options__item cap-header__signout" />
        </OptionsMenu>
      </div>
    </header>
  );
}
